import { withProps } from '@prophecy/utils/react/hoc';
import { BoldPlugin, CodePlugin, ItalicPlugin, StrikethroughPlugin, SubscriptPlugin, SuperscriptPlugin, UnderlinePlugin } from '@udecode/plate-basic-marks/react';
import { BlockquotePlugin } from '@udecode/plate-block-quote/react';
import { CodeBlockPlugin, CodeLinePlugin, CodeSyntaxPlugin } from '@udecode/plate-code-block/react';
import { HEADING_KEYS } from '@udecode/plate-heading';
import { HeadingPlugin } from '@udecode/plate-heading/react';
import { HighlightPlugin } from '@udecode/plate-highlight/react';
import { HorizontalRulePlugin } from '@udecode/plate-horizontal-rule/react';
import { BulletedListPlugin, ListItemPlugin, ListPlugin, NumberedListPlugin } from '@udecode/plate-list/react';
import { MarkdownPlugin } from '@udecode/plate-markdown';
import { ImagePlugin } from '@udecode/plate-media/react';
import { MentionInputPlugin, MentionPlugin } from '@udecode/plate-mention/react';
import { TableCellHeaderPlugin, TableCellPlugin, TablePlugin, TableRowPlugin } from '@udecode/plate-table/react';
import { TrailingBlockPlugin } from '@udecode/plate-trailing-block';
import { PlateLeaf, ParagraphPlugin } from '@udecode/plate/react';
import sql from 'highlight.js/lib/languages/sql';
import { createLowlight } from 'lowlight';
import { MENTION_TRIGGER } from '../Mention';
import { MentionElement, MentionInputElement } from '../Mention/MentionComponents';
import { CodeBlockElement, CodeLineElement, CodeSyntaxLeaf } from './CodeElement/CodeBlockElement';
import { HrElement } from './HRElement';
import { ImageAndEmbeddingsWrapperElement } from './ImageAndEmbeddingsWrapperElement';
import { CustomLinkPlugin } from './LinkPlugin';
import { PlateElementWithId } from './PlateElementWithId';
// Lowlight setup for code syntax highlighting
export const lowlight = createLowlight();
lowlight.register('sql', sql);
export const CodeBlockPluginWithPrism = CodeBlockPlugin.configure({
    options: {
        lowlight
    }
});
export const baseMarkdownPlugins = [
    HeadingPlugin,
    BlockquotePlugin,
    CodeBlockPluginWithPrism,
    BoldPlugin,
    ItalicPlugin,
    UnderlinePlugin,
    CodePlugin,
    StrikethroughPlugin,
    SubscriptPlugin,
    SuperscriptPlugin,
    HorizontalRulePlugin,
    ImagePlugin,
    CustomLinkPlugin,
    ParagraphPlugin,
    HighlightPlugin,
    TablePlugin.configure({
        options: {
            disableMerge: true
        }
    }),
    TableRowPlugin,
    TableCellPlugin,
    TableCellHeaderPlugin,
    ListPlugin,
    TrailingBlockPlugin,
    MarkdownPlugin,
    MentionPlugin.configure({
        options: {
            trigger: MENTION_TRIGGER,
            triggerPreviousCharPattern: /^$|^[\s"']$/
        }
    }),
    MentionInputPlugin
];
export const baseComponentOverrides = {
    [BlockquotePlugin.key]: withProps(PlateLeaf, {
        as: 'blockquote'
    }),
    [BoldPlugin.key]: withProps(PlateLeaf, {
        as: 'strong'
    }),
    [CodeBlockPlugin.key]: CodeBlockElement,
    [CodeLinePlugin.key]: CodeLineElement,
    [CodePlugin.key]: withProps(PlateLeaf, {
        as: 'code'
    }),
    [CodeSyntaxPlugin.key]: CodeSyntaxLeaf,
    [HEADING_KEYS.h1]: withProps(PlateElementWithId, {
        as: 'h1'
    }),
    [HEADING_KEYS.h2]: withProps(PlateElementWithId, {
        as: 'h2'
    }),
    [HEADING_KEYS.h3]: withProps(PlateElementWithId, {
        as: 'h3'
    }),
    [HEADING_KEYS.h4]: withProps(PlateElementWithId, {
        as: 'h4'
    }),
    [HEADING_KEYS.h5]: withProps(PlateElementWithId, {
        as: 'h5'
    }),
    [HEADING_KEYS.h6]: withProps(PlateElementWithId, {
        as: 'h6'
    }),
    [HighlightPlugin.key]: withProps(PlateLeaf, {
        as: 'mark'
    }),
    [HorizontalRulePlugin.key]: HrElement,
    [ImagePlugin.key]: ImageAndEmbeddingsWrapperElement,
    [ItalicPlugin.key]: withProps(PlateLeaf, {
        as: 'i'
    }),
    [CustomLinkPlugin.key]: withProps(PlateLeaf, {
        as: 'a'
    }),
    [ParagraphPlugin.key]: withProps(PlateLeaf, {
        as: 'p'
    }),
    [StrikethroughPlugin.key]: withProps(PlateLeaf, {
        as: 's'
    }),
    [SubscriptPlugin.key]: withProps(PlateLeaf, {
        as: 'sub'
    }),
    [SuperscriptPlugin.key]: withProps(PlateLeaf, {
        as: 'sup'
    }),
    [TableCellHeaderPlugin.key]: withProps(PlateLeaf, {
        as: 'th'
    }),
    [TableCellPlugin.key]: withProps(PlateLeaf, {
        as: 'td'
    }),
    [TablePlugin.key]: withProps(PlateLeaf, {
        as: 'table'
    }),
    [TableRowPlugin.key]: withProps(PlateLeaf, {
        as: 'tr'
    }),
    [UnderlinePlugin.key]: withProps(PlateLeaf, {
        as: 'u'
    }),
    [BulletedListPlugin.key]: withProps(PlateLeaf, {
        as: 'ul'
    }),
    [ListItemPlugin.key]: withProps(PlateLeaf, {
        as: 'li'
    }),
    [NumberedListPlugin.key]: withProps(PlateLeaf, {
        as: 'ol'
    }),
    [MentionPlugin.key]: MentionElement,
    [MentionInputPlugin.key]: MentionInputElement
};
